"use client";

import Tooltip from "@/components/Tooltip";
import { useState } from "react";

export interface TimelineEvent {
  id: string;
  tooltip?: string;
  date?: string;
  x: number;
  y: number;
  color?: string;
  curve?: 'cubic' | 'linear' | 'quadratic' | 'step' | 'easeInOut' | 'easeIn' | 'easeOut' | 'easeOutIn';
  children?: TimelineEvent[];
  label?: string;
  nodeSize?: number;
}

const mainColor = '#000';
const raceColor = '#8B0000';
const slowdownColor = '#2A623D';

// a branching timeline of events
// events with a set "color" are treated as more central/important
export const events: TimelineEvent[] = [
  {
    id: '0-1', x: 0, y: 0,
    tooltip: '2025: The first real AI agents',
    color: mainColor,
    children: [
      {
        id: '1-1', x: 25, y: 0,
        tooltip: '2026: Better, fairly reliable AI agents',
        color: mainColor,
        children: [
          {
            id: '2-1', x: 45, y: 0,
            tooltip: 'Early 2027: Automated AI researchers',
            color: mainColor,
            children: [
              {
                id: '3-9', x: 67, y: -18, curve: 'easeOutIn', color: 'transparent',
                tooltip: '2027: International Conflict',
              },
              {
                id: '3-1', x: 65, y: -5,
                tooltip: 'Late 2027: Misalignment discovered',
                color: slowdownColor,
                children: [
                  {
                    id: '5-1', x: 95, y: -9,
                    tooltip: 'Resources reallocated late 2027, misalignments fixed early 2028, aligned ASI transforms the world for the better under the direction of a privileged few over the next five years', curve: 'linear',
                    color: slowdownColor,
                  },
                  {
                    id: '4-7', x: 92, y: 0, curve: 'easeIn',
                    tooltip: 'Resources reallocated late 2027, it\'s not enough to solve the underlying problems, rivals catch up',
                  },
                  {
                    id: '4-8', x: 94, y: -17, curve: 'linear',
                    tooltip: 'Resources reallocated late 2027, misalignments fixed early 2028, aligned ASI transforms the world for the better in a transparent & democratic way over the next five years',
                  },
                  {
                    id: '4-10', x: 75, y: -12, curve: 'linear',
                    tooltip: 'Late 2027: Geopolitical tensions over the AI race escalates to war',
                    children: [
                      {
                        id: '6-1', x: 82, y: -18, color: 'transparent', curve: 'linear',
                        tooltip: '2028: War over Taiwan',
                      }
                    ]
                  }
                ]
              },
              {
                id: '3-3', x: 65, y: 5,
                tooltip: 'Late 2027: Misalignment goes undiscovered',
                color: raceColor,
                children: [
                  {
                    id: '4-3', x: 95, y: 9,
                    tooltip: 'Late 2027: misaligned AI builds misaligned superintelligence', curve: 'linear',
                    color: raceColor,
                  },
                  {
                    id: '4-4', x: 93, y: 20, curve: 'easeIn',
                    tooltip: 'Late 2027: AI vs. AI conflict', 
                  }
                ]
              },
            ]
          },
          {
            id: '2-2', x: 35, y: 5, curve: 'easeIn',
            tooltip: '2026: Slower progress than we expect; agents still mostly useless',
            children: [
              {
                id: '3-2', x: 60, y: 13, curve: 'linear',
                tooltip: '2027: AI Progress slowed further by conflict over Taiwan',
                children: [
                  {
                    id: '4-1', x: 77, y: 17, curve: 'easeOutIn',
                    tooltip: '2028: US and china neck-and-neck', color: 'transparent'
                  },
                  {
                    id: '4-2', x: 73, y: 24, curve: 'easeOutIn',
                    tooltip: '2028: US pulls decisively ahead of China', color: 'transparent'
                  }
                ]
              },
              {
                id: '3-8', x: 55, y: 30, curve: 'easeOutIn',
                tooltip: '2027: AI winter looms', color: 'transparent'
              },
              {
                id: '3-4', x: 52, y: 17, curve: 'linear',
                tooltip: '2027: Growth continues but barriers to AGI are becoming clear',
                children: [
                  {
                    id: '4-5', x: 65, y: 23, curve: 'linear', color: 'transparent',
                    tooltip: '2028: Economy booms thanks to integration of narrow AI systems'
                  },
                  {
                    id: '4-6', x: 60, y: 27, curve: 'easeIn', color: 'transparent',
                    tooltip: '2028: Dramatic conceptual breakthrough accelerates progress again',
                  }
                ]
              }
            ]
          },
          {
            id: '2-3', x: 40, y: -6.5, curve: 'easeOutIn',
            tooltip: '2026: Faster progress than we expect; full AGI achieved',
            children: [
              {
                id: '3-6', x: 53, y: -12, curve: 'linear',
                tooltip: 'Late 2026: Intelligence explosion, weights stolen, misaligned AI takeover',
                color: 'transparent'
              },
              {
                id: '3-7', x: 50, y: -15, curve: 'easeIn',
                tooltip: 'Late 2026: Intelligence explosion, weights stolen, temporary deal reached',
                color: 'transparent'
              }
            ]
          }
        ]
      }
    ]
  }
];

// we only turn this on for debugging
const showLabels = false

// New component for rendering a node
const TimelineNode = ({ event, x, y }: { event: TimelineEvent, x: number, y: number }) => {
  const [isHovered, setIsHovered] = useState(false);
  const nodeSize = event.nodeSize || (event.color && event.color !== 'transparent' ? 2 : 1.5);
  const tooltipContent = <div className="text-sm px-3 py-1">{event.tooltip}</div>;
  
  return (
    <Tooltip content={tooltipContent}>
      <circle
        cx={x}
        cy={y}
        r={nodeSize}
        fill={event.color || '#aaa'}
        stroke="#fff"
        strokeWidth={1}
        // style={{ 
        //   cursor: 'pointer',
        //   filter: isHovered ? 'brightness(0.6)' : 'brightness(1)',
        // }}
        // onMouseOver={() => setIsHovered(true)}
        // onMouseOut={() => setIsHovered(false)}
      />
    </Tooltip>
  );
};

// New component for rendering connections with improved hover area
const TimelineConnection = ({ event, parentEvent, startX, startY, endX, endY }: { 
  event: TimelineEvent, 
  parentEvent: TimelineEvent, 
  startX: number, 
  startY: number, 
  endX: number, 
  endY: number 
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const tooltipContent = <div className="text-sm px-3 py-1">{event.tooltip}</div>;
  
  // Fix for visibility: ensure all paths have a proper color
  // If color is 'transparent', use a light gray instead of actually making it transparent
  const lineColor = event.color === 'transparent' ? '#aaa' : (event.color || '#888');
  const lineWidth = event.color && event.color !== 'transparent' ? 4.5 : 3;
  
  // Simplify path generation to ensure it works
  let pathDefinition = `M ${startX} ${startY} L ${endX} ${endY}`;
  
  // Only apply curves if explicitly requested
  if (event.curve && event.curve !== 'linear') {
    const xDiff = endX - startX;
    const yDiff = endY - startY;
    const midX = startX + xDiff / 2;
    
    switch (event.curve) {
      case 'quadratic':
        pathDefinition = `M ${startX} ${startY} Q ${midX} ${startY} ${endX} ${endY}`;
        break;
      case 'cubic':
        pathDefinition = `M ${startX} ${startY} C ${startX + xDiff/3} ${startY} ${endX - xDiff/3} ${endY} ${endX} ${endY}`;
        break;
      case 'easeInOut':
        pathDefinition = `M ${startX} ${startY} C ${startX + xDiff/3} ${startY} ${endX - xDiff/3} ${endY} ${endX} ${endY}`;
        break;
      case 'easeIn':
        pathDefinition = `M ${startX} ${startY} C ${startX + xDiff*0.25} ${startY} ${endX - xDiff*0.1} ${endY} ${endX} ${endY}`;
        break;
      case 'easeOut':
        pathDefinition = `M ${startX} ${startY} C ${startX + xDiff*0.1} ${startY} ${endX - xDiff*0.25} ${endY} ${endX} ${endY}`;
        break;
      case 'easeOutIn':
        pathDefinition = `M ${startX} ${startY} C ${startX + xDiff*0.25} ${startY} ${endX - xDiff*0.25} ${endY} ${endX} ${endY}`;
        break;
      case 'step':
        pathDefinition = `M ${startX} ${startY} H ${midX} V ${endY} H ${endX}`;
        break;
      default:
        pathDefinition = `M ${startX} ${startY} L ${endX} ${endY}`;
    }
  }
  
  return (
    <Tooltip content={tooltipContent}>
      <g 
        onMouseOver={() => setIsHovered(true)}
        onMouseOut={() => setIsHovered(false)}
        style={{ cursor: 'pointer' }}
      >
        {/* Invisible wider path for hover detection (10px stroke) */}
        <path
          d={pathDefinition}
          stroke="transparent"
          strokeWidth={8}  // Much wider than visible path for easier hovering
          fill="none"
          style={{ pointerEvents: 'stroke' }}
        />
        
        {/* Visible path */}
        <path
          d={pathDefinition}
          stroke={lineColor}
          strokeWidth={lineWidth}
          fill="none"
          style={{ 
            opacity: event.color === 'transparent' ? 0.6 : 1,
            strokeDasharray: event.color === 'transparent' ? '3,2' : 'none',
            pointerEvents: 'none',  // This prevents the visible path from capturing mouse events
            filter: isHovered ? 'brightness(0.6)' : 'brightness(1)',
          }}
          vectorEffect="non-scaling-stroke"
        />
      </g>
    </Tooltip>
  );
};

const TimelineDiagram = ({ height = 200 }: { height?: number }) => {
  // Find min and max values from all events for both x and y
  const findBounds = (eventList: TimelineEvent[]): { minX: number, maxX: number, minY: number, maxY: number } => {
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    
    const traverse = (events: TimelineEvent[]) => {
      events.forEach(event => {
        minX = Math.min(minX, event.x);
        maxX = Math.max(maxX, event.x);
        minY = Math.min(minY, event.y);
        maxY = Math.max(maxY, event.y);
        if (event.children) {
          traverse(event.children);
        }
      });
    };
    
    traverse(eventList);
    return { minX, maxX, minY, maxY };
  };

  const { minX, maxX, minY, maxY } = findBounds(events);
  const xRange = maxX - minX;
  const yRange = maxY - minY;
  
  // Scale coordinates to fit SVG viewBox (with padding)
  const scaleX = (x: number) => {
    const horizontalPadding = 1.5; // Padding on left and right
    return horizontalPadding + ((x - minX) / xRange) * (350 - 2 * horizontalPadding);
  };
  
  const scaleY = (y: number) => {
    const verticalPadding = 10; // Padding on top and bottom
    return verticalPadding + ((y - minY) / yRange) * (100 - 2 * verticalPadding);
  };

  // Debug function to verify we're processing all nodes
  const debugCountNodes = (events: TimelineEvent[]): number => {
    let count = 0;
    const traverse = (nodes: TimelineEvent[]) => {
      nodes.forEach(node => {
        count++;
        if (node.children && node.children.length > 0) {
          traverse(node.children);
        }
      });
    };
    traverse(events);
    return count;
  };
  
  // Debug total node count
  const totalNodes = debugCountNodes(events);

  const generateConnections = () => {
    const connections: JSX.Element[] = [];
    const nodes: JSX.Element[] = [];
    let processedNodeCount = 0;
    let connectionCount = 0;
    
    const traverseEvents = (eventList: TimelineEvent[], parentEvent: TimelineEvent | null) => {
      eventList.forEach(event => {
        processedNodeCount++;
        const x = scaleX(event.x);
        const y = scaleY(event.y);
        
        // Add node
        nodes.push(
          <TimelineNode 
            key={`node-${event.id}`} 
            event={event} 
            x={x} 
            y={y} 
          />
        );
        
        // Add connection to parent if exists
        if (parentEvent) {
          connectionCount++;
          const startX = scaleX(parentEvent.x);
          const startY = scaleY(parentEvent.y);
          
          connections.push(
            <TimelineConnection
              key={`connection-${parentEvent.id}-${event.id}`}
              event={event}
              parentEvent={parentEvent}
              startX={startX}
              startY={startY}
              endX={x}
              endY={y}
            />
          );
        }
        
        // Recursively process children
        if (event.children && event.children.length > 0) {
          traverseEvents(event.children, event);
        }
      });
    };
    
    // Start traversal from root events
    traverseEvents(events, null);
    
    // Group connections by their type to control the z-order
    // First: paths without color (regular connections)
    // Second: paths with transparent color (dashed lines)
    // Third: paths with solid color (colored non-dashed lines)
    const dashedConnections = connections.filter(conn => 
      conn.props.event.color === 'transparent' || 
      !conn.props.event.color
    );
    
    const solidConnections = connections.filter(conn => 
      conn.props.event.color && 
      conn.props.event.color !== 'transparent'
    );
    
    // Return elements in z-order: dashed at bottom, solid on top, nodes above all
    return [...dashedConnections, ...solidConnections, ...nodes];
  };
  
  return (
    <div className="w-full relative -mt-[40px] md:-mt-[60px] lg:-mt-[15px] -mb-[10px] md:-mb-[5px]">
      <svg
        width="100%"
        height={height}
        viewBox="0 0 350 100"
        preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block' }}
      >
        {generateConnections()}
      </svg>
      <div className="text-lg italic text-gray-500 absolute left-[8px] bottom-[35px]">
        Just a few of many possible futures...
      </div>

      {/* Render any UI you don't want to get squished by the svg stretching */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none' }}>
        {renderEvents(events)}
      </div>
    </div>
  );
};

const renderEvents = (eventList: TimelineEvent[]) => {
  // Find min and max values
  const findBounds = (eventList: TimelineEvent[]): { minX: number, maxX: number, minY: number, maxY: number } => {
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    
    const traverse = (events: TimelineEvent[]) => {
      events.forEach(event => {
        minX = Math.min(minX, event.x);
        maxX = Math.max(maxX, event.x);
        minY = Math.min(minY, event.y);
        maxY = Math.max(maxY, event.y);
        if (event.children) {
          traverse(event.children);
        }
      });
    };
    
    traverse(eventList);
    return { minX, maxX, minY, maxY };
  };

  const { minX, maxX, minY, maxY } = findBounds(eventList);
  const xRange = maxX - minX;
  const yRange = maxY - minY;
  
  const scaleX = (x: number) => {
    const horizontalPadding = 5;
    return horizontalPadding + ((x - minX) / xRange) * (100 - 2 * horizontalPadding);
  };
  
  const scaleY = (y: number) => {
    const verticalPadding = 0;
    return verticalPadding + ((y - minY) / yRange) * (100 - 2 * verticalPadding);
  };

  const elements: JSX.Element[] = [];

  const traverseEvents = (events: TimelineEvent[]) => {
    events.forEach((event, i) => {
      const left = `${scaleX(event.x)}%`;  // Scale the x coordinate as percentage
      const top = `${scaleY(event.y)}%`;   // Scale the y coordinate as percentage
      const labelTop = event.y > 0 ? 20 : -20;
      elements.push(
        <div key={`${event.id}`} style={{ position: 'absolute', left, top, transform: 'translate(-50%, -50%)', display: 'flex', alignItems: 'center', width: 'auto', pointerEvents: 'auto' }}>
          {showLabels && <div style={{ position: 'relative', top: '-10px', fontSize: 9, fontFamily: 'monospace', fontWeight: 'bold', opacity: 0.5, marginLeft: '5px' }}>
            {event.id}
          </div>}
          <div style={{ position: 'relative', top: labelTop, fontSize: 11, fontWeight: 'bold', whiteSpace: 'nowrap', opacity: 0.5, marginLeft: 0 }}>
            {event.label}
          </div>
        </div>
      );
      if (event.children) {
        traverseEvents(event.children);
      }
    });
  };

  traverseEvents(eventList);
  return elements;
};

export default TimelineDiagram;
